<template>
  <div>
    <!--  add     :color="'var(--darker-primary)'"-->
    <VuePhoneNumberInput
      ref="phoneNumber"
      v-model="phoneNumber"
      :dark="isDark"
      :size="size"
      error-color="#ea5455"
      dir="ltr"
      :no-use-browser-locale="!defaultCountryCodeComputed"
      :no-example="noExample"
      :valid-color="phoneInputValidColor"
      :fetch-country="fetchCountry && !value"
      :error="!valid && !!phoneNumber"
      :ignored-countries="ignoredCountries"
      :preferred-countries="preferredCountries"
      :default-country-code="defaultCountryCodeComputed"
      :translations="translations"
      :only-countries="onlyCountries"
      :disabled="disabled"
      @update="handleUpdate"
    />
    <small
      v-if="!valid && showInvalidFeedback"
      class="text-danger"
      v-text="$t('validations.invalid-phone-number')"
    />
  </div>
</template>

<script>
import(/* webpackPrefetch: 0 */ /* webpackPreload: 0 */ /* webpackChunkName: "phone-input" */ 'vue-phone-number-input/dist/vue-phone-number-input.css')

export default {
  name: 'OkPhoneNumberInput',
  components: { VuePhoneNumberInput: () => import(/* webpackPrefetch: 0 */ /* webpackPreload: 0 */ /* webpackChunkName: "phone-input" */ 'vue-phone-number-input') },
  props: {
    value: {
      type: [String],
      required: false,
      default: () => '',
    },
    fetchCountry: {
      type: Boolean,
      default: false,
    },
    state: {
      type: Boolean,
      default: true,
    },
    noExample: {
      type: Boolean,
      default: false,
    },
    defaultCountryCode: {
      type: [String],
      default: null,
    },
    size: {
      type: [String],
      default: null,
    },
    onlyCountries: {
      type: Array,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      valid: false,
      showInvalidFeedback: false,
      phoneNumber: '',
      countryCode: '',
      formatInternational: '',
      defaultCountryCodeComputed: null,
      translations: {
        countrySelectorLabel: this.$t('fields.country'),
        phoneNumberLabel: this.$t('fields.phone'),
      },
    }
  },
  computed: {
    phoneInputValidColor() {
      return this.isDark ? '#676d7d' : '#d8d6de'
    },
    preferredCountries() {
      return ['KW', 'AE', 'SA', 'QA', 'OM', 'BH', 'EG', 'JO', 'LB', 'SY', 'DZ']
    },
    ignoredCountries() {
      return ['IL', 'AS', 'AD', 'AI', 'AG', 'AW', 'BS', 'BB', 'BZ', 'BM', 'IO', 'VG', 'BI', 'CV', 'BQ', 'KY', 'CX', 'CC', 'KM', 'CD', 'CK', 'CW', 'DJ', 'GQ', 'ER', 'FK', 'FO', 'GF', 'PF', 'GD', 'GU', 'GT', 'GG', 'GW', 'IM', 'JE', 'KI', 'LS', 'LR', 'MO', 'MK', 'MG', 'MQ', 'MH', 'MU', 'YT', 'NR', 'NC', 'NI', 'NF', 'NU', 'MP', 'MW', 'PW', 'PG', 'RE', 'BL', 'SH', 'KN', 'LC', 'MF', 'PM', 'VC', 'WS', 'ST', 'SM', 'SR', 'TL', 'TK', 'TC', 'TV', 'VI', 'VU', 'WF', 'EH', 'AX']
    },
  },
  watch: {
    value(number) {
      if (number === '') this.phoneNumber = ''
      this.setPhoneNumber(number)
    },
    defaultCountryCode() {
      this.setDefaultCountryCode()
    },
    valid(valid) {
      this.$emit('update:valid', valid)
    },
    '$i18n.locale': function () {
      this.translations = {
        countrySelectorLabel: this.$t('fields.country'),
        phoneNumberLabel: this.$t('fields.phone'),
      }
    },
  },
  mounted() {
    this.setPhoneNumber(this.value)
    this.setDefaultCountryCode()
  },
  methods: {
    handleUpdate(payload) {
      this.checkNumWithCustomRegex(payload.phoneNumber)
      this.countryCode = payload.countryCode ?? this.countryCode
      this.showInvalidFeedback = false
      this.valid = this.countryCode === 'KW' ? this.checkNumWithCustomRegex(payload.phoneNumber) : payload.isValid
      this.formatInternational = payload.formatInternational || ''
      this.$emit('input', payload?.e164 || '')
      this.$emit('update:country-calling-code', payload.countryCallingCode)
    },
    getInternationalFormat() {
      return this.formatInternational
    },
    setPhoneNumber(number) {
      if (this.phoneNumber) {
        return
      }
      this.valid = true
      this.phoneNumber = number
    },
    validate() {
      if (!this.value && !this.required) return true

      this.showInvalidFeedback = !this.valid
      return this.valid
    },
    getCountryDetails(code) {
      const country = this.$refs.phoneNumber?.codesCountries?.find(c => c.iso2 === (code || this.countryCode))
      return {
        country_iso: country?.iso2,
        country: country?.name,
      }
    },
    getCountryFromDialCode(dialCode) {
      const country = this.$refs.phoneNumber?.codesCountries?.find(c => c.dialCode === dialCode)
      return {
        country_iso: country?.iso2,
        country: country?.name,
      }
    },
    setDefaultCountryCode() {
      setTimeout(() => {
        if (this.value) {
          this.defaultCountryCodeComputed = null
          return
        }

        const defaultCountry = this.defaultCountryCode || this.$store.getters['branches/currentBranch']?.country_iso || null

        if (defaultCountry) {
          this.defaultCountryCodeComputed = defaultCountry
        } else {
          this.defaultCountryCodeComputed = null
        }
      }, 2000)
    },
    checkNumWithCustomRegex(phone) { // this function to check new tele company in Kuwait (not supported by plugin)
      const regex = '^[56942]\\d{7}$'
      return phone ? phone.replace(/ /g, '').match(regex) : phone
    },
  },
}
</script>

<style lang="scss">

.input-tel:not(.has-error):not(.is-valid).input-phone-number,
.country-selector.input-country-selector {
  .country-selector__list__item.selected {
    background-color: var(--okmenu-color) !important;
  }

  input {
    background: transparent !important;
  }

  &.is-dark input {
    border-color: var(--theme-dark-input-border-color) !important;
  }

  &:hover {
    border-color: var(--okmenu-color) !important;
  }

  &.is-focused {
    input {
      caret-color: var(--okmenu-color) !important;
      box-shadow: unset!important;
      border-color: var(--okmenu-color) !important;
    }

    label {
      color: var(--okmenu-color) !important;
    }
  }
}

.country-selector.lg .country-selector__input, .input-tel.lg .input-tel__input{
  height: 48px!important;
  min-height: 48px!important;
  font-size: 1.143rem!important;
  line-height: 1.25!important;
}

.input-tel.lg .input-tel__label {
  font-size: 11px!important;
}

.input-tel__input {
  background-color: transparent!important;
  .dark-layout & {
    border-color: var(--theme-dark-input-border-color)!important;
  }
}

</style>
